/**
 * Unified instrument type utilities.
 * Consolidates the 3 separate nomenclature maps (equipment, line, untagged)
 * into a single set of helpers for the unified InstrumentEditor.
 */

import instrumentSchema from '../schemas/instrument-schema.json';
import {
  InstrumentCategory,
  TAGGED_INSTRUMENT_CATEGORIES,
  TAGGED_INSTRUMENT_NOMENCLATURE,
  UntaggedInstrumentType,
  UNTAGGED_INSTRUMENT_NOMENCLATURE,
  SUBTYPE_MAPPING,
  UNTAGGED_SUBTYPE_MAPPING,
  canonicalizeInstrumentType,
} from '../types';

const VISIBILITY = instrumentSchema.visibility_rules;
const IDENTITY_DISPLAY_RULES = instrumentSchema.identity_display_rules;

// === Optgroup structure for unified dropdown ===

export interface InstrumentTypeOption {
  value: string;
  label: string;
}

export interface InstrumentTypeGroup {
  group: string;
  options: InstrumentTypeOption[];
}

/**
 * Returns all instrument types grouped for a unified dropdown.
 * Groups: Equipment Instruments, Line Instruments, Untagged Instruments.
 */
export function getUnifiedInstrumentTypeGroups(): InstrumentTypeGroup[] {
  return [
    {
      group: 'Tagged Instruments',
      options: TAGGED_INSTRUMENT_CATEGORIES.map((code) => ({
        value: code,
        label: TAGGED_INSTRUMENT_NOMENCLATURE[code],
      })),
    },
    {
      group: 'Untagged Instruments',
      options: (Object.keys(UNTAGGED_INSTRUMENT_NOMENCLATURE) as UntaggedInstrumentType[]).map((code) => ({
        value: code,
        label: UNTAGGED_INSTRUMENT_NOMENCLATURE[code],
      })),
    },
  ];
}

/**
 * Flat list of all instrument type options (for AutocompleteDropdown).
 */
export function getUnifiedInstrumentTypeOptions(): InstrumentTypeOption[] {
  const groups = getUnifiedInstrumentTypeGroups();
  return groups.flatMap((g) => g.options);
}

/**
 * Get subtypes for any instrument type string (equipment, line, or untagged).
 */
export function getSubtypesForAnyType(instrumentType: string | null | undefined): string[] {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return [];

  // Check untagged mapping first (more specific keys like 'IV', 'NRV')
  if (canonicalType in UNTAGGED_SUBTYPE_MAPPING) {
    return UNTAGGED_SUBTYPE_MAPPING[canonicalType] || [];
  }

  // Check direct SUBTYPE_MAPPING
  if (canonicalType in SUBTYPE_MAPPING) {
    return SUBTYPE_MAPPING[canonicalType];
  }

  return [];
}

/**
 * Whether the contextual type selector should be shown for this instrument type.
 * Rules loaded from schemas/instrument-schema.json visibility_rules.
 */
const CONTEXTUAL_TYPE_APPLICABLE_SET = new Set<string>(VISIBILITY.contextual_type_applicable_types);
export function isContextualTypeApplicable(
  instrumentType: string | null | undefined,
  contextType?: string | null
): boolean {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return false;
  if (contextType === VISIBILITY.contextual_type_excluded_context_type) return false;
  return CONTEXTUAL_TYPE_APPLICABLE_SET.has(canonicalType);
}

/**
 * Whether isolation valve tags should be shown for this instrument type.
 */
const ISOLATION_VALVE_APPLICABLE_SET = new Set<string>(VISIBILITY.isolation_valve_applicable_types);
export function isIsolationValveApplicable(instrumentType: string | null | undefined): boolean {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return false;
  return ISOLATION_VALVE_APPLICABLE_SET.has(canonicalType);
}

const TEXT_PRIMARY_INSTRUMENT_TYPES = new Set<string>(IDENTITY_DISPLAY_RULES.text_primary_types);
const GENERATED_ID_PRIMARY_INSTRUMENT_TYPES = new Set<string>(IDENTITY_DISPLAY_RULES.generated_id_primary_types);

export function isTextPrimaryInstrumentType(instrumentType: string | null | undefined): boolean {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return false;
  return TEXT_PRIMARY_INSTRUMENT_TYPES.has(canonicalType);
}

export function isGeneratedIdPrimaryInstrumentType(instrumentType: string | null | undefined): boolean {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return false;
  return GENERATED_ID_PRIMARY_INSTRUMENT_TYPES.has(canonicalType);
}

/**
 * Whether the safety-valve upstream/downstream fields should be shown.
 */
const SAFETY_VALVE_FIELDS_APPLICABLE_SET = new Set<string>(VISIBILITY.safety_valve_fields_applicable_types);
export function isSafetyValveFieldsApplicable(instrumentType: string | null | undefined, contextType: string | null | undefined): boolean {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  if (!canonicalType) return false;
  return SAFETY_VALVE_FIELDS_APPLICABLE_SET.has(canonicalType) && contextType === VISIBILITY.safety_valve_fields_required_context_type;
}

/**
 * Get the display name for any instrument type code.
 * Merges all 3 nomenclature maps.
 */
export function getInstrumentDisplayName(code: string | null | undefined): string {
  const canonicalCode = canonicalizeInstrumentType(code);
  if (!canonicalCode) return '';
  if (canonicalCode in TAGGED_INSTRUMENT_NOMENCLATURE) {
    return TAGGED_INSTRUMENT_NOMENCLATURE[canonicalCode as InstrumentCategory];
  }
  if (canonicalCode in UNTAGGED_INSTRUMENT_NOMENCLATURE) {
    return UNTAGGED_INSTRUMENT_NOMENCLATURE[canonicalCode as UntaggedInstrumentType];
  }
  return canonicalCode;
}
